import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import {
  ACTION_BY_FILTERS_KEY,
  DEFAULT_WORKED_LIST_VIEW,
} from "@/Constants/common";
import {
  _getWorkedCandidates,
  _getWorkedShifts,
} from "@/services/clientcandidates";

import {
  formatBookedWorkedCandidates,
  formatExpandedShifts,
} from "../../utilities/dataFormatter";

const initialState = {
  isLoading: false,
  isPaginationLoading: false,
  workedCandidates: [],
  selectedCandidate: [], // list of candidate id
  selectedContract: [], // list of signoff ids
  filters: {
    search: "",
    sessionType: [],
    candidateReferenceNo: "",
    contractReferenceNo: "",
    candidateId: [],
    timeSheetStatus: [],
    candidateRole: null,
    complianceStatus: null,
    miles: null,
    candidateLicenseNo: "",
    candidatePostcode: "",
    workedDateFrom: "",
    workedDateTo: "",
    contractDateFrom: "",
    contractDateTo: "",
    ...ACTION_BY_FILTERS_KEY,
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  totalCount: 0,
  isSelectedAll: false,
  listView: DEFAULT_WORKED_LIST_VIEW,
  showFilters: false,
  toggleSelected: false,
};

export const fetchWorkedCandidates = createAsyncThunk(
  "candidatesWorked/fetchWorkedCandidates",
  async (data, { dispatch, getState }) => {
    const {
      clientCandidatesWorked: {
        pagination,
        filters,
        listView,
        sorting,
        workedCandidates,
      },
    } = getState();
    if (_.isEmpty(data)) {
      data = { ...filters };
    }
    if (pagination.isLastPage) {
      return dispatch(setWorkedCandidatesLoading(false));
    }
    // Set loading states based on page number
    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));
    const _data = {
      ...data,
      ...sorting,
      currentPage: pagination.currentPage,
      listView,
      timeSheetStatus: listView === "shifts" ? data.timeSheetStatus : [],
    };
    const response = _getWorkedCandidates(_data);
    response
      .then((res) => {
        const {
          data: { worked_list, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        const data = formatBookedWorkedCandidates(worked_list, "worked");
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList = currentPage === 1 ? [] : _.uniq(workedCandidates);
        let firstContractId = data.length > 0 ? data[0].contract_id : null;
        dispatch(updateTotalCount(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.contract_id === firstContractId) !==
            undefined
        ) {
          //
        } else {
          dispatch(updateWorkedCandidatesList([...prevList, ...data]));
        }
        return res;
      })
      .catch((error) => {
        // handleAPICatch(error);
        return rejectWithValue(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setWorkedCandidatesLoading(false));
          dispatch(setPaginationLoading(false)); // Set pagination loading to true
        }, 15);
      });
  },
);

export const fetchWorkedShifts = createAsyncThunk(
  "candidatesWorked/fetchWorkedShifts",
  async (data, thunkAPI) => {
    const { contractId } = data;
    const response = _getWorkedShifts(data);
    response
      .then((res) => {
        const {
          data: { worked_contract_shift_list },
        } = res;
        const data = formatExpandedShifts(worked_contract_shift_list, "worked");
        thunkAPI.dispatch(
          updateWorkedShifts({
            contract_id: contractId,
            shifts: data,
          }),
        );
      })
      .catch((error) => {
        // handleAPICatch(error);
        return thunkAPI.rejectWithValue(error);
      });
  },
);

const candidatesWorkedSlice = createSlice({
  name: "candidatesWorked",
  initialState,
  reducers: {
    /**
     * Update the worked candidates list in the state and set isLoading to false.
     *
     * @param {type} state - the current state
     * @param {type} action - the action containing the payload
     * @return {type} undefined
     */
    updateWorkedCandidatesList: (state, action) => {
      state.workedCandidates = action.payload;
      state.isLoading = false;
    },
    /**
     * Set the loading state for worked candidates.
     *
     * @param {type} state - the state object
     * @param {type} action - the action object
     * @return {type} description of return value
     */
    setWorkedCandidatesLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload; // New reducer for pagination loading
    },
    /**
     * Updates the selected candidate in the state.
     *
     * @param {type} state - the current state
     * @param {type} action - the action object
     * @return {type} undefined
     */
    updateSelectedCandidate: (state, action) => {
      state.selectedCandidate = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    /**
     * Updates the selected sign off ids in the state.
     *
     * @param {type} state - the current state
     * @param {type} action - the action object
     * @return {type} undefined
     */
    updateSelectedContract: (state, action) => {
      state.selectedContract = action.payload;
    },
    /**
     * Deselects all candidates.
     *
     * @param {type} state - description of state parameter
     * @param {type} action - description of action parameter
     * @return {type} description of return value
     */
    deSelectAllCandidate: (state, action) => {
      state.selectedCandidate = [];
      state.selectedContract = [];
      state.isSelectedAll = false;
      state.toggleSelected = !state.toggleSelected;
    },
    /**
     * Returns an array of selected candidate IDs based on the provided state and action.
     *
     * @param {object} state - the state object
     * @param {object} action - the action object
     * @return {array} an array of selected candidate IDs
     */
    getSelectedCandidatesIDS: (state, action) => {
      let selectedIDs = [];
      state.selectedCandidate.forEach((item) => {
        selectedIDs.push(
          state.workedCandidates.find((candidate) => candidate.email === item)
            .id,
        );
      });
      return selectedIDs;
    },
    /**
     * Updates the filters in the state based on the provided action payload.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object containing the payload
     */
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    /**
     * Function to show the filter modal and update state accordingly.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object triggering the modal display
     */
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    /**
     * Updates the pagination state with the given payload.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object
     * @return {void}
     */
    updatePagination: (state, action) => {
      state.pagination = action.payload;
    },
    /**
     * A function to update the state filters.
     *
     * @param {type} state - the current state
     * @param {type} action - the action to be performed
     * @return {type} undefined
     */
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    /**
     * Update the toggleSelected property in the state object.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object
     * @return {undefined}
     */
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    updateListView: (state, action) => {
      state.listView = action.payload;
    },
    updateWorkedShifts: (state, action) => {
      const { contract_id, shifts } = action.payload;
      let workedClone = [...state.workedCandidates];
      state.workedCandidates = workedClone.map((ele) => {
        if (ele.contract_id == contract_id) {
          ele.shifts = shifts;
        }
        return ele;
      });
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    updateCandidateStatus: (state, action) => {
      let { unsuitable, sortlisted, isSelectedAll, ignoreIds, ids } =
        action.payload;
      let _ids = ids.map((id) => id.toString());
      let _ignoreIds = ignoreIds.map((id) => id.toString());
      state.workedCandidates = state.workedCandidates.map((candidate) => {
        if (_ignoreIds.includes(candidate?.candidate_id?.toString())) {
          return candidate;
        }
        if (
          _ids.includes(candidate?.candidate_id?.toString()) ||
          isSelectedAll
        ) {
          candidate.candidate_unsuitable = unsuitable;
          candidate.candidate_sortlisted = sortlisted;
        }
        return candidate;
      });
    },
  },
});

export const {
  updateWorkedCandidatesList,
  setWorkedCandidatesLoading,
  setPaginationLoading,
  updateSelectedCandidate,
  deSelectAllCandidate,
  getSelectedCandidatesIDS,
  updateFilters,
  updateSorting,
  updateSelectedContract,
  showFilterModal,
  updatePagination,
  reStateFilters,
  updateToggleSelected,
  updateListView,
  updateWorkedShifts,
  updateTotalCount,
  toggleSelectedAll,
  updateCandidateStatus,
} = candidatesWorkedSlice.actions;

export default candidatesWorkedSlice.reducer;
